import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/community';

/**
 * Community Tip Tracker Service (Admin)
 *
 * Handles all API calls for resulting and managing community tips submitted by
 * users. Mirrors the user-facing Results Tracker service but operates over the
 * shared `community_tips` table and requires the ADMIN role.
 *
 * NOTE: This service is intentionally separate from `resultsTrackerService` so
 * that the existing user-facing tracker is never affected.
 */

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Handle API errors consistently across all community-tip endpoints.
 */
const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        // Authentication errors
        if (status === 401) {
            const errorCode = data.error?.code || data.error || 'AUTH_FAILED';
            return {
                success: false,
                status,
                errorCode,
                message: data.message || data.error?.message || 'Authentication failed',
                isAuthError: true
            };
        }

        // Authorization errors (caller is not an admin)
        if (status === 403) {
            return {
                success: false,
                status,
                errorCode: data.error || 'ADMIN_REQUIRED',
                message: data.message || 'Admin access is required to manage community tips.',
                isForbidden: true
            };
        }

        // Validation errors
        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid data',
                details: data.details || []
            };
        }

        // Not found errors (USER_NOT_FOUND / TOOL_NOT_FOUND / TIP_NOT_FOUND)
        if (status === 404) {
            return {
                success: false,
                status,
                errorCode: data.error || 'NOT_FOUND',
                message: data.message || 'Resource not found'
            };
        }

        // Rate limiting
        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many requests. Please slow down and try again shortly.'
            };
        }

        // Server errors
        return {
            success: false,
            status,
            errorCode: data.error || 'SERVER_ERROR',
            message: data.error?.message || data.message || 'Server error. Please try again later.'
        };
    }

    // Network errors
    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const communityTipsTrackerService = {
    /**
     * Get all community tips across every tipper.
     * @returns {Promise} API response with { tips }
     */
    getAllData: async () => {
        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}/tips`, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Manually add a community tip on behalf of a specified tipper.
     * @param {object} tip - Tip data (must include user_id + tipped_tool)
     * @returns {Promise} API response with the created tip
     */
    addBet: async (tip) => {
        try {
            const response = await axios.post(`${API_URL}${BASE_PATH}/bets`, tip, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Bulk add community tips on behalf of a single tipper.
     * @param {number} userId - The tipper for all tips in this batch
     * @param {array} bets - Array of tip objects (each carries its own tipped_tool)
     * @param {boolean} replaceExisting - Whether to replace the tipper's existing tips
     * @returns {Promise} API response with { imported, duplicatesSkipped, tips }
     */
    bulkImportBets: async (userId, bets, replaceExisting = false) => {
        try {
            const response = await axios.post(`${API_URL}${BASE_PATH}/bets/bulk`, {
                user_id: userId,
                bets,
                replaceExisting
            }, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Full update of an existing tip, addressed by its global id.
     * The tipper (user_id) is immutable and must NOT be sent.
     * @param {number} id - Global community tip id
     * @param {object} tip - Updated tip data (must include tipped_tool)
     * @returns {Promise} API response with the updated tip
     */
    updateBet: async (id, tip) => {
        try {
            const response = await axios.put(`${API_URL}${BASE_PATH}/bets/${id}`, tip, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Delete a tip by its global id.
     * @param {number} id - Global community tip id
     * @returns {Promise} API response
     */
    deleteBet: async (id) => {
        try {
            const response = await axios.delete(`${API_URL}${BASE_PATH}/bets/${id}`, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                message: response.data.message || 'Community tip deleted successfully'
            };
        } catch (error) {
            return handleApiError(error);
        }
    },

    /**
     * Reorder a single tipper's tips within a date.
     * Must include every tip for that tipper + date.
     * @param {number} userId - The tipper whose tips are being reordered
     * @param {string} date - Date in YYYY-MM-DD format
     * @param {array} betOrders - Array of { id, sort_order } (global tip ids)
     * @returns {Promise} API response with reorder result
     */
    reorderBets: async (userId, date, betOrders) => {
        try {
            const response = await axios.patch(`${API_URL}${BASE_PATH}/bets/reorder`, {
                user_id: userId,
                date,
                betOrders
            }, {
                headers: getAuthHeaders()
            });

            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default communityTipsTrackerService;
